
import { Component, Prop, Vue } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import { TCase_Create } from "@/models/cases";

import { SUCCESS_NOTIFICATION_TYPE } from "@/constants/notification";
import { SMCases, SMNps, SMUser } from "@/store";
import { API_TCompany } from "@/models/cases_expert_companies";
import PackageItem from "@/components/V2/WidgetSkilledWorkerVisa/PackageItem.vue";

const CHECKMARK_ICON = "checkmark-sharp";
const PLUS_ICON = "add";

const SUCCESS_TYPE = "success";

const EMPLOYER_COS_SERVICE_REQUEST_ALIAS = "employer_cos";
const EMPLOYER_SW_SERVICE_REQUEST_ALIAS = "employer_sw";
const EMPLOYER_SW_COS_SERVICE_REQUEST_ALIAS = "employer_sw_cos";

const EMPLOYER_SW_START_VISA_ALIAS = "skilled_worker";
const EMPLOYER_SW_COS_START_VISA_ALIAS = "cos_sw";
const EMPLOYER_SW_INSIDE_START_VISA_ALIAS = "sw_inside";
const EMPLOYER_SW_COS_INSIDE_START_VISA_ALIAS = "cos_sw_inside";

const INITIAL_SERVICE_REQUEST_OPTION = 2;

// const SERVICE_ROUTE = "CandidateService";

@Component({
  components: {
    PackageItem,
  },
})
export default class NewWidgetSkilledWorkerVisa extends Vue {
  @Prop({ type: Boolean, default: false }) readonly value!: boolean;
  @Prop({ type: Boolean, default: false }) readonly isOutside!: boolean;
  @Prop({ type: Number, default: 0 }) readonly id!: number;

  loading = false;

  selectedOption = INITIAL_SERVICE_REQUEST_OPTION;

  show = false;

  mounted() {
    this.$root.$on("open-start-sw-popup", () => {
      this.openPopup();
    });
  }

  openPopup() {
    this.show = true;

    this.$emit("input", true);
  }

  closePopup(bool: boolean): void {
    if (bool) return;

    this.show = false;

    this.$emit("input", false);
  }

  setLoading(service, isLoading: boolean) {
    this.loading = isLoading;

    const index = this.services.findIndex((item) => item.id === service.id);

    if (index < 0) return;

    this.services[index].loading = isLoading;
  }

  getIsSecondaryButton(alias: string) {
    return alias === EMPLOYER_SW_SERVICE_REQUEST_ALIAS;
  }

  async submitRequest(service) {
    try {
      this.setLoading(service, true);

      if (!this.id) return;

      const alias = service.alias;

      await this.axios.get(API_ROUTES.CASE_SERVICE_REQUEST(this.id, alias));
    } finally {
      this.setLoading(service, false);

      this.$emit("close");
    }
  }

  getStartServiceAlias(service) {
    // sw outside
    if (service.alias === EMPLOYER_SW_SERVICE_REQUEST_ALIAS && this.isOutside)
      return EMPLOYER_SW_START_VISA_ALIAS;

    // sw inside
    if (service.alias === EMPLOYER_SW_SERVICE_REQUEST_ALIAS && !this.isOutside)
      return EMPLOYER_SW_INSIDE_START_VISA_ALIAS;

    // sw cos outside
    if (service.alias === EMPLOYER_SW_COS_SERVICE_REQUEST_ALIAS && this.isOutside)
      return EMPLOYER_SW_COS_START_VISA_ALIAS;

    // sw cos inside
    if (service.alias === EMPLOYER_SW_COS_SERVICE_REQUEST_ALIAS && !this.isOutside)
      return EMPLOYER_SW_COS_INSIDE_START_VISA_ALIAS;

    return "";
  }

  aliasMap: Record<string, string> = {
    skilled_worker: "skilled_worker",
    cos_sw: "skilled_worker_cos",
    sw_inside: "skilled_worker_cos_inside",
    cos_sw_inside: "skilled_worker_cos_inside",
  };

  async startVisaApplication(service) {
    try {
      if (!this.id) return;

      const alias = this.getStartServiceAlias(service);

      const storageKey = this.aliasMap[alias];

      if (storageKey) {
        await SMNps.removeFromLocalStorage(storageKey);
      }

      this.setLoading(service, true);

      const params = {
        alias,
        case_from_id: this.id,
        company_id: ((SMCases.case as unknown) as API_TCompany).company_id ?? 0,
      };

      const { data } = await this.axios.get<number | TCase_Create>(
        API_ROUTES.CASE_CREATE_COS_SW(params)
      );

      if (!data) return;

      let caseId = 0;

      if (typeof data === "number") caseId = data;

      if (typeof data === "object") caseId = data?.case_id ?? 0;

      if (!caseId) return;

      await this.$router.push(`/cases/${caseId}/dashboard/${SMCases.tabId}`);

      this.$root.$emit("show-notification", {
        type: SUCCESS_NOTIFICATION_TYPE,
        title: this.$tc("toaster"),
      });

      SMUser.updateCurrentUser();
    } finally {
      this.setLoading(service, false);

      this.closePopup(false);
    }
  }

  services = [
    {
      id: 1,
      alias: EMPLOYER_COS_SERVICE_REQUEST_ALIAS,
      title: this.$t("service_1.title"),
      benefitsTitle: this.$t("service_1.benefitsTitle"),
      benefits: [
        {
          icon: CHECKMARK_ICON,
          label: this.$t("service_1.benefits.benefit_1"),
        },
      ],
      paragraph: "",
      feeTitle: this.$t("service_1.feeTitle"),
      fee: this.isOutside ? this.$t("service_1.feeOutside") : this.$t("service_1.feeInside"),
      feeDescription: this.$t("service_1.feeDescription"),
      button: this.$t("service_1.button"),
      background: false,
      loading: false,
      action: this.submitRequest,
    },
    {
      id: 2,
      alias: EMPLOYER_SW_SERVICE_REQUEST_ALIAS,
      title: this.$t("service_2.title"),
      benefitsTitle: this.$t("service_2.benefitsTitle"),
      benefits: [
        {
          icon: CHECKMARK_ICON,
          label: this.$t("service_2.benefits.benefit_1"),
        },
        {
          icon: CHECKMARK_ICON,
          label: this.$t("service_2.benefits.benefit_2"),
        },
        {
          icon: CHECKMARK_ICON,
          label: this.$t("service_2.benefits.benefit_3"),
        },
        {
          icon: CHECKMARK_ICON,
          label: this.$t("service_2.benefits.benefit_4"),
        },
        {
          icon: CHECKMARK_ICON,
          label: this.$t("service_2.benefits.benefit_5"),
        },
      ],
      paragraph: "",
      feeTitle: this.$t("service_2.feeTitle"),
      fee: this.$t("service_2.fee"),
      feeDescription: this.$t("service_2.feeDescription"),
      button: this.$t("service_2.button"),
      background: false,
      loading: false,
      action: this.startVisaApplication,
    },
    {
      id: 3,
      alias: EMPLOYER_SW_COS_SERVICE_REQUEST_ALIAS,
      dark: true,
      title: this.$t("service_3.title"),
      benefitsTitle: this.$t("service_3.benefitsTitle"),
      benefits: [
        {
          icon: CHECKMARK_ICON,
          label: this.$t("service_3.benefits.benefit_1"),
        },
        {
          icon: PLUS_ICON,
          label: this.$t("service_3.benefits.benefit_2"),
          type: SUCCESS_TYPE,
        },
      ],
      paragraph: "",
      feeTitle: this.$t("service_3.feeTitle"),
      fee: this.$t("service_3.fee"),
      feeDescription: this.$t("service_3.feeDescription"),
      button: this.$t("service_3.button"),
      background: true,
      loading: false,
      action: this.startVisaApplication,
    },
  ];
}
